// Состояние Трекера (README §5, §13): задачи и доски — с сервера; вид, фильтр,
// выбранная доска и ширины колонок — у клиента. Ширины и выбор доски/вида
// переживают перезагрузку (localStorage['abx-tracker']), фильтр — нет.

import { create } from 'zustand'
import { ApiError } from '../../api'
import {
  trackerV3,
  type ColumnId,
  type CreateItem,
  type PatchItem,
  type TrackerConfig,
  type TrackerItem,
  type TrackerViewMode,
} from './types'

const KEY = 'abx-tracker'
export const COL_W_DEFAULT = 288
export const COL_W_MIN = 200
export const COL_W_MAX = 560

interface Saved {
  board?: string
  view?: TrackerViewMode
  colW?: Partial<Record<ColumnId, number>>
}

function readSaved(): Saved {
  try {
    const raw = localStorage.getItem(KEY)
    const data = raw ? (JSON.parse(raw) as Saved) : {}
    return data && typeof data === 'object' ? data : {}
  } catch {
    return {}
  }
}

function writeSaved(patch: Saved): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...readSaved(), ...patch }))
  } catch {
    // приватный режим — не критично
  }
}

function errorText(cause: unknown, fallback: string): string {
  return cause instanceof ApiError ? cause.message : fallback
}

const saved = readSaved()

interface TrackerState {
  config: TrackerConfig | null
  items: TrackerItem[]
  loaded: boolean
  error: string | null
  board: string
  view: TrackerViewMode
  /** id статуса-метки или null — «все» */
  filter: string | null
  colW: Partial<Record<ColumnId, number>>
  /** доска, чьё название сейчас правится на месте (после «+ доска») */
  editBoard: string | null

  load: () => Promise<void>
  setBoard: (id: string) => void
  setView: (view: TrackerViewMode) => void
  setFilter: (id: string | null) => void
  setColW: (col: ColumnId, w: number) => void
  setEditBoard: (id: string | null) => void
  create: (body: CreateItem) => Promise<boolean>
  patch: (body: PatchItem) => Promise<void>
  remove: (path: string) => Promise<void>
  addBoard: () => Promise<void>
  renameBoard: (id: string, title: string) => Promise<void>
  dropBoard: (id: string) => Promise<void>
  addLabel: (title: string) => Promise<boolean>
  dropLabel: (id: string) => Promise<void>
  clearError: () => void
}

export const useTracker = create<TrackerState>((set, get) => ({
  config: null,
  items: [],
  loaded: false,
  error: null,
  board: saved.board ?? 'main',
  view: saved.view ?? 'board',
  filter: null,
  colW: saved.colW ?? {},
  editBoard: null,

  load: async () => {
    try {
      const [config, items] = await Promise.all([trackerV3.config(), trackerV3.items()])
      const board = config.boards.some((b) => b.id === get().board) ? get().board : config.boards[0]?.id ?? 'main'
      const filter = get().filter
      set({
        config,
        items,
        board,
        loaded: true,
        error: null,
        filter: filter && config.labels.some((l) => l.id === filter) ? filter : null,
      })
    } catch (cause) {
      set({ loaded: true, error: errorText(cause, 'Не удалось загрузить трекер') })
    }
  },

  setBoard: (board) => {
    writeSaved({ board })
    // с доски в архиве пользователь уходит смотреть доску, а не её архив
    const view = get().view === 'archive' ? 'board' : get().view
    writeSaved({ view })
    set({ board, view, filter: null })
  },
  setView: (view) => {
    writeSaved({ view })
    set({ view })
  },
  setFilter: (filter) => set({ filter }),
  setColW: (col, w) => {
    const colW = { ...get().colW, [col]: Math.round(Math.min(COL_W_MAX, Math.max(COL_W_MIN, w))) }
    writeSaved({ colW })
    set({ colW })
  },
  setEditBoard: (editBoard) => set({ editBoard }),

  create: async (body) => {
    try {
      const item = await trackerV3.create({ ...body, board: body.board ?? get().board })
      set({ items: [...get().items, item], error: null })
      return true
    } catch (cause) {
      set({ error: errorText(cause, 'Задача не создана') })
      return false
    }
  },

  patch: async (body) => {
    const before = get().items
    // оптимистично: перенос и закрытие видны сразу, сервер лишь подтверждает
    // срок сервер пересчитывает сам (конец дня, «просрочено») — его не угадываем
    const { due: _due, ...local } = body
    set({
      items: before.map((it) =>
        it.path === body.path
          ? { ...it, ...local, closedAt: body.closed === true ? Date.now() / 1000 : body.closed === false ? null : it.closedAt }
          : it,
      ),
    })
    try {
      const item = await trackerV3.patch(body)
      set({ items: get().items.map((it) => (it.path === item.path ? item : it)), error: null })
    } catch (cause) {
      set({ items: before, error: errorText(cause, 'Изменение не сохранилось') })
    }
  },

  remove: async (path) => {
    const before = get().items
    set({ items: before.filter((it) => it.path !== path) })
    try {
      await trackerV3.remove(path)
    } catch (cause) {
      set({ items: before, error: errorText(cause, 'Задача не удалена') })
    }
  },

  addBoard: async () => {
    const taken = new Set(get().config?.boards.map((b) => b.title) ?? [])
    let title = 'Новая доска'
    for (let n = 2; taken.has(title); n++) title = `Новая доска ${n}`
    try {
      const board = await trackerV3.addBoard(title)
      const config = get().config
      if (config) set({ config: { ...config, boards: [...config.boards, board] } })
      get().setBoard(board.id)
      set({ editBoard: board.id, error: null })
    } catch (cause) {
      set({ error: errorText(cause, 'Доска не создана') })
    }
  },

  renameBoard: async (id, title) => {
    const config = get().config
    const name = title.trim()
    set({ editBoard: null })
    if (!config || !name || config.boards.find((b) => b.id === id)?.title === name) return
    set({ config: { ...config, boards: config.boards.map((b) => (b.id === id ? { ...b, title: name } : b)) } })
    try {
      await trackerV3.renameBoard(id, name)
    } catch (cause) {
      set({ config, error: errorText(cause, 'Доска не переименована') })
    }
  },

  dropBoard: async (id) => {
    try {
      await trackerV3.dropBoard(id)
      await get().load()
    } catch (cause) {
      set({ error: errorText(cause, 'Доска не удалена') })
    }
  },

  addLabel: async (title) => {
    const config = get().config
    if (!config) return false
    try {
      const label = await trackerV3.addLabel(title)
      set({ config: { ...config, labels: [...config.labels, label] }, error: null })
      return true
    } catch (cause) {
      set({ error: errorText(cause, 'Статус не добавлен') })
      return false
    }
  },

  dropLabel: async (id) => {
    try {
      await trackerV3.dropLabel(id)
      if (get().filter === id) set({ filter: null })
      await get().load()
    } catch (cause) {
      set({ error: errorText(cause, 'Статус не удалён') })
    }
  },

  clearError: () => set({ error: null }),
}))

/** Задачи доски в порядке колонок и `order`. */
export function boardItems(items: TrackerItem[], board: string): TrackerItem[] {
  return items
    .filter((it) => it.board === board)
    .sort((a, b) => a.order - b.order || a.created - b.created)
}

/** Порядок для вставки на место `index` среди `list` (без перетаскиваемой). */
export function orderAt(list: TrackerItem[], index: number): number {
  if (list.length === 0) return 1
  if (index <= 0) return list[0].order - 1
  if (index >= list.length) return list[list.length - 1].order + 1
  return (list[index - 1].order + list[index].order) / 2
}
